import {
  DocumentNode,
  OperationVariables,
  useFragment,
  useQuery,
} from '@apollo/client';

import { getError, getTypeOrNull } from './apollo';

// Define a basic shape for GraphQL errors returned within the data object
type GraphQLErrorShape = {
  __typename: 'Error';
  message?: string | null;
};

export type StandardRefHookOptions = {
  name: string;
  skip?: boolean;
};

// TItem is the type of the data *at* dataPath
// TPath is the key under which TItem will be returned
type StandardRefHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem | undefined;
} & {
  error?: Error;
  isLoading: boolean;
};

export function withStandardRefHook<
  TData extends { [K in TPath]?: TItem | GraphQLErrorShape | null | undefined },
  TItem,
  TPath extends string,
  TVariables extends OperationVariables & { name: string },
>(
  queryDoc: DocumentNode,
  dataPath: TPath,
  fragmentDoc: DocumentNode,
  fragmentName: string,
  typename: string,
): (options: StandardRefHookOptions) => StandardRefHookResult<TItem, TPath> {
  return (
    options: StandardRefHookOptions,
  ): StandardRefHookResult<TItem, TPath> => {
    const { data: fragmentData, complete } = useFragment<TItem>({
      fragment: fragmentDoc,
      fragmentName,
      from: options.skip
        ? null
        : {
            __typename: typename,
            name: options.name,
          },
    });

    const {
      data,
      error: networkError,
      loading,
    } = useQuery<TData, TVariables>(queryDoc, {
      variables: { name: options.name } as TVariables,
      skip: options.skip || complete,
      fetchPolicy: 'cache-first',
    });

    // If fragment is complete in cache, return cached data
    if (complete) {
      return {
        [dataPath]: fragmentData as TItem,
        error: undefined,
        isLoading: false,
      } as StandardRefHookResult<TItem, TPath>;
    }

    // Get the data directly from the path, type is inferred as TItem | null
    const typedData = getTypeOrNull(data?.[dataPath] as any) ?? undefined;
    const appError = getError(data?.[dataPath] as any) ?? undefined;

    return {
      [dataPath]: typedData as TItem | undefined,
      error: appError ?? networkError,
      isLoading: loading,
    } as StandardRefHookResult<TItem, TPath>;
  };
}
